import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QRCodeCanvas } from "qrcode.react";
import { playAnnouncement, enableAnnouncerSound } from "@/lib/announcer";
import { Volume2, Maximize2, RotateCcw, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_LABEL = {
  waiting: "Waiting",
  in_progress: "Now Serving",
  billing: "Billing / Cashier",
  done: "Done",
  cancelled: "Cancelled",
};

export default function QueueDisplay() {
  // Self-service join: the TV shows the clinic's walk-in kiosk QR. A patient
  // scans it with their own phone, picks a doctor there, and appears on this
  // very screen a few seconds later (the queue polls every 3s).
  const { user } = useAuth();
  const hospitalId = user?.active_hospital_id || "";
  const kioskUrl = hospitalId ? `${window.location.origin}/kiosk/${hospitalId}` : "";

  const [queue, setQueue] = useState([]);
  const [latest, setLatest] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const lastPlayedId = useRef("");

  const load = async () => {
    try {
      const [q, a] = await Promise.all([
        api.get("/queue/today"),
        api.get("/queue/announcements/latest"),
      ]);
      setQueue(q.data || []);
      setLatest(a.data || null);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!audioEnabled || !latest?.id || latest.id === lastPlayedId.current) return;
    lastPlayedId.current = latest.id;

    // The TV is where waiting patients listen, so it repeats each call once.
    // Interactive stations play once — repeats everywhere made one call sound
    // like four overlapping ones.
    playAnnouncement(latest, { repeat: 2 });
  }, [latest, audioEnabled]);

  const grouped = {
    waiting: queue.filter((q) => q.status === "waiting"),
    in_progress: queue.filter((q) => q.status === "in_progress"),
    billing: queue.filter((q) => q.status === "billing"),
  };

  const fullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  // Enabling audio needs a user gesture; prime speechSynthesis so later calls speak.
  const enableAudio = () => {
    setAudioEnabled(true);
    enableAnnouncerSound();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950 text-white p-8 overflow-auto" data-testid="queue-display-page">
      {!audioEnabled ? (
        <button onClick={enableAudio} className="fixed inset-0 z-[10000] bg-slate-950/95 flex flex-col items-center justify-center text-white cursor-pointer" data-testid="enable-audio-overlay">
          <Volume2 size={72} className="mb-5 text-blue-300" />
          <div className="text-4xl font-bold">Tap to enable sound</div>
          <div className="text-slate-300 mt-3 text-lg">Do this once so patient names can be called out loud.</div>
        </button>
      ) : null}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-sm uppercase tracking-[0.35em] text-blue-300">eMedbay Queue Display</div>
          <h1 className="text-5xl font-bold mt-2">Now Serving</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={enableAudio}>
            <Volume2 size={16} className="mr-2" />
            {audioEnabled ? "Audio Enabled" : "Enable Audio"}
          </Button>
          <Button variant="secondary" onClick={load}>
            <RotateCcw size={16} className="mr-2" /> Refresh
          </Button>
          <Button variant="secondary" onClick={fullscreen}>
            <Maximize2 size={16} className="mr-2" /> Fullscreen
          </Button>
        </div>
      </div>

      <div className="bg-blue-600 rounded-3xl p-8 mb-8 shadow-2xl flex items-center gap-8">
        <div className="flex-1 min-w-0">
          <div className="text-blue-100 uppercase tracking-[0.3em] text-sm">Latest Call</div>
          <div className="text-6xl font-black mt-3">
            {latest?.patient_name || "Waiting for next call"}
          </div>
          <div className="text-3xl mt-4 text-blue-50">
            {latest?.destination || latest?.doctor_name || ""}
          </div>
          {latest?.message ? (
            <div className="text-lg mt-5 text-blue-100">{latest.message}</div>
          ) : null}
        </div>

        {kioskUrl ? (
          <div className="shrink-0 bg-white rounded-2xl p-5 text-center shadow-lg" data-testid="queue-kiosk-qr">
            {/* White box is not decoration: QR codes need a light quiet zone to scan. */}
            <QRCodeCanvas value={kioskUrl} size={210} includeMargin />
            <div className="mt-3 text-slate-900 font-bold text-xl flex items-center justify-center gap-2">
              <Smartphone size={20} className="text-blue-600" /> Scan to join the queue
            </div>
            <div className="text-slate-500 text-sm mt-1 max-w-[230px] mx-auto">
              Register and choose your doctor on your phone — no need to line up.
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {["waiting", "in_progress", "billing"].map((status) => (
          <div key={status} className="bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 bg-white/10">
              <div className="text-2xl font-bold">{STATUS_LABEL[status]}</div>
              <div className="text-sm text-slate-300">{grouped[status].length} patient(s)</div>
            </div>
            <div className="divide-y divide-white/10">
              {grouped[status].slice(0, 10).map((q) => (
                <div key={q.id} className="p-5">
                  <div className="text-2xl font-semibold">{q.patient_snapshot?.full_name || "—"}</div>
                  <div className="text-slate-300 mt-1">{q.assigned_doctor_name || ""}</div>
                </div>
              ))}
              {grouped[status].length === 0 ? (
                <div className="p-6 text-slate-400 italic">No patients</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
